import { effect, inject, Injectable, OnDestroy, signal, WritableSignal } from '@angular/core';
import { GrowthBook, WidenPrimitives } from '@growthbook/growthbook';
import { isNil, omitBy } from 'lodash';
import { environment } from 'src/environments/environment';
import { Tenant } from 'src/environments/evironment.interface';
import { accountexternal } from '../client/client';
import { MixpanelService } from '../mix-panel.service/mix-panel.service';

@Injectable({
  providedIn: 'root',
})
export class GrowthbookService implements OnDestroy {
  private mixpanel = inject(MixpanelService);
  private gb: WritableSignal<GrowthBook | undefined> = signal(undefined);
  private attributes = signal<Record<string, any>>({});
  private initPromise: Promise<unknown> | null = null;

  constructor() {
    // Sync attribute changes with growthbook
    effect(() => {
      const gb = this.gb();
      const attributes = this.attributes();

      if (gb && attributes && this.hasAttributesChanged(attributes)) {
        this.gb()?.setAttributes(attributes);
      }
    });
  }

  public async initGrowthBook(
    user?: accountexternal.GetUserResponse | null,
    tenant?: Tenant | undefined,
    triage?: boolean | undefined,
    isStaff?: boolean
  ) {
    if (!environment?.growthbook) return;
    if (this.gb()) this.logOut();
    this.gb.set(
      new GrowthBook({
        apiHost: environment?.growthbook?.apiHost,
        clientKey: environment?.growthbook?.clientKey,
        enableDevMode: !environment.production,
        trackingCallback: (experiment, result) => {
          if (isStaff || !result.inExperiment) return;

          this.mixpanel.setSuperProperties({ [`experiment.${result.featureId}`]: result.value });

          this.mixpanel.track('$experiment_started', {
            'experiment.id': experiment.key,
            'variant.id': result.variationId,
            $source: 'growthbook',
          });
        },
      })
    );

    this.updateAttributes(
      omitBy(
        {
          id: user?.doc_id,
          platform: 'web',
          tenant: tenant?.friendly_id,
          activePetIsTriage: triage,
        },
        isNil
      )
    );

    this.initPromise = this.gb()!.init({ streaming: true });
    await this.initPromise;
    return this.initPromise;
  }

  public logOut() {
    if (!this.gb()) {
      return;
    }
    this.gb()?.destroy();
    this.gb.set(undefined);
    this.initPromise = null;
  }

  public whenReady(timeoutMs = 3000): Promise<boolean> {
    const deadline = Date.now() + timeoutMs;
    return new Promise<boolean>((resolve) => {
      const check = () => {
        if (!this.gb()) {
          if (Date.now() >= deadline) {
            resolve(false);
            return;
          }
          setTimeout(check, 50);
          return;
        }
        const p = this.initPromise;
        if (!p) {
          resolve(true);
          return;
        }
        const timeoutId = setTimeout(() => resolve(false), Math.max(0, deadline - Date.now()));
        p.then(
          () => {
            clearTimeout(timeoutId);
            resolve(true);
          },
          () => {
            clearTimeout(timeoutId);
            resolve(false);
          }
        );
      };
      check();
    });
  }

  public updateAttributes(attributes: Record<string, any>) {
    const currentAttributes = this.gb()?.getAttributes() || {};
    const newAttributes = { ...currentAttributes, ...attributes };
    this.attributes.set(newAttributes);
  }

  public experimentOn(experimentKey: string): boolean {
    return this.gb()?.isOn(experimentKey) || false;
  }

  public experimentValue<T = unknown>(experimentKey: string, defaultValue?: T): WidenPrimitives<T> | undefined {
    return this.gb()?.getFeatureValue<T | undefined, string>(experimentKey, defaultValue);
  }

  public featureValue<T = unknown>(featureKey: string, defaultValue: T): WidenPrimitives<T> {
    const gb = this.gb();
    if (!gb) {
      return defaultValue as WidenPrimitives<T>;
    }
    return gb.getFeatureValue<T, string>(featureKey, defaultValue);
  }

  ngOnDestroy() {
    if (this.gb()) {
      this.gb()?.destroy();
    }
  }

  private hasAttributesChanged(attributes: Record<string, any>): boolean {
    const currentAttributes = this.gb()?.getAttributes() || {};
    return JSON.stringify(currentAttributes) !== JSON.stringify(attributes);
  }

  get growthBook() {
    return this.gb;
  }
}
